import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bookmark, Menu, X, ChevronDown, Calendar } from "lucide-react";
import { useInspiration } from "../hooks/useStore";

export default function Navbar({ onInspirationOpen }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { list } = useInspiration();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  const navBg =
    isHome && !scrolled
      ? "bg-transparent"
      : "bg-cream-50/95 backdrop-blur-md border-b border-cream-200 shadow-sm";

  const textColor = isHome && !scrolled ? "text-white" : "text-matte-900";
  const logoColor = isHome && !scrolled ? "text-white" : "text-matte-900";

  const serviceLinks = [
    { label: "Overview", to: "/services" },
    { label: "01 · Consulting", to: "/services/consulting" },
    { label: "02 · Design", to: "/services/design" },
    { label: "03 · Build & Deliver", to: "/services/build" },
  ];

  const savedCount = list.length;

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between gap-4 h-16 sm:h-20">
          <Link to="/" className="flex flex-col leading-none flex-shrink-0" aria-label="Ananda Paudel — home">
            <span
              className={`font-serif text-lg sm:text-2xl font-light tracking-[0.22em] sm:tracking-widest transition-colors duration-300 ${logoColor}`}
            >
              ANANDA PAUDEL
            </span>
            <span
              className={`hidden xl:block font-sans text-[9px] tracking-[0.22em] uppercase font-medium transition-colors duration-300 ${
                isHome && !scrolled ? "text-cream-300" : "text-walnut-400"
              }`}
            >
              CONSULT · DESIGN · BUILD
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <Link
              to="/"
              className={`nav-link font-sans text-sm tracking-wider transition-colors ${textColor} ${
                location.pathname === "/" ? "active" : ""
              }`}
            >
              Home
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className={`nav-link font-sans text-sm tracking-wider flex items-center gap-1 transition-colors ${textColor} ${
                  location.pathname.startsWith("/services") ? "active" : ""
                }`}
                aria-haspopup="true"
                aria-expanded={servicesOpen}
              >
                Services{" "}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-cream-50 border border-cream-200 shadow-xl rounded-sm w-60 py-2 z-50">
                  {serviceLinks.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      className="block px-5 py-2.5 text-sm font-sans text-matte-700 hover:text-walnut-500 hover:bg-cream-100 transition-colors"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              to="/portfolio"
              className={`nav-link font-sans text-sm tracking-wider transition-colors ${textColor} ${
                location.pathname.startsWith("/portfolio") ? "active" : ""
              }`}
            >
              Portfolio
            </Link>
            <Link
              to="/about"
              className={`nav-link font-sans text-sm tracking-wider transition-colors ${textColor} ${
                location.pathname === "/about" ? "active" : ""
              }`}
            >
              About
            </Link>
            <Link
              to="/contact"
              className={`nav-link font-sans text-sm tracking-wider transition-colors ${textColor} ${
                location.pathname === "/contact" ? "active" : ""
              }`}
            >
              Contact
            </Link>
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={onInspirationOpen}
              className={`relative transition-colors hover:opacity-70 ${textColor}`}
              aria-label="Inspiration list"
              title="Your inspiration list"
            >
              <Bookmark size={20} />
              {savedCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-walnut-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {savedCount}
                </span>
              )}
            </button>
            <Link
              to="/book"
              className="hidden sm:inline-flex items-center gap-1.5 bg-walnut-500 hover:bg-walnut-600 text-white font-sans text-xs tracking-wider uppercase px-4 py-2.5 rounded-sm transition-colors"
            >
              <Calendar size={14} />
              Book Consultation
            </Link>
            <button
              className={`lg:hidden transition-colors ${textColor}`}
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-50 lg:hidden transition-all duration-300 ${
          mobileOpen ? "visible" : "invisible"
        }`}
      >
        <div
          className={`absolute inset-0 bg-matte-900/50 transition-opacity duration-300 ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 bottom-0 w-72 bg-cream-50 shadow-2xl transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="p-6 border-b border-cream-200 flex items-center justify-between">
            <span className="font-serif text-lg text-matte-900">Menu</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="text-matte-500 hover:text-matte-900"
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>
          <div className="p-6 flex flex-col gap-1">
            <Link
              to="/"
              className="py-3 font-sans text-sm tracking-wider text-matte-700 border-b border-cream-200"
            >
              Home
            </Link>
            <div className="py-2">
              <p className="text-xs tracking-widest text-matte-400 uppercase mb-2 mt-1">
                Services
              </p>
              {serviceLinks.map((s) => (
                <Link
                  key={s.to}
                  to={s.to}
                  className="block py-2 font-sans text-sm text-matte-600 hover:text-walnut-500 pl-3"
                >
                  {s.label}
                </Link>
              ))}
            </div>
            <Link
              to="/portfolio"
              className="py-3 font-sans text-sm tracking-wider text-matte-700 border-t border-cream-200"
            >
              Portfolio
            </Link>
            <Link
              to="/about"
              className="py-3 font-sans text-sm tracking-wider text-matte-700 border-t border-cream-200"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="py-3 font-sans text-sm tracking-wider text-matte-700 border-t border-b border-cream-200"
            >
              Contact
            </Link>
            <Link
              to="/book"
              className="mt-5 inline-flex items-center justify-center gap-1.5 bg-walnut-500 hover:bg-walnut-600 text-white font-sans text-xs tracking-wider uppercase px-4 py-3 rounded-sm transition-colors"
            >
              <Calendar size={14} />
              Book a Free Consultation
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
